import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Input } from 'components/input';
import DateTimeInput from 'components/input/DateTimeInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';

import {
  LTLAccessorial,
  LTLSelectedAccessorial,
  LTL_FREIGHT_CLASSES,
} from '../types';
import { BatchLTLBulkEditValues, BatchLTLStackableEdit } from './types';

// Tai pickup times are wall-clock times, as in the single Tai form.
const TAI_WALL_CLOCK_TIMEZONE = 'UTC';

const EMPTY_BULK_EDIT: BatchLTLBulkEditValues = {
  originLocation: '',
  destinationLocation: '',
  pickupDate: null,
  pickupCloseDate: null,
  freightClass: '',
  addAccessorialCode: '',
  removeAccessorialCode: '',
  stackable: BatchLTLStackableEdit.Unchanged,
};

type BatchLTLBulkEditProps = {
  selectedCount: number;
  accessorials: LTLAccessorial[];
  // Accessorials on at least one selected shipment, for "Remove".
  selectedAccessorials: LTLSelectedAccessorial[];
  onApply: (values: BatchLTLBulkEditValues) => void;
  onCancel: () => void;
};

export function BatchLTLBulkEdit({
  selectedCount,
  accessorials,
  selectedAccessorials,
  onApply,
  onCancel,
}: BatchLTLBulkEditProps) {
  const formMethods = useForm<BatchLTLBulkEditValues>({
    defaultValues: EMPTY_BULK_EDIT,
  });
  const { control, register, setValue, getValues } = formMethods;
  const values = useWatch({ control }) as BatchLTLBulkEditValues;
  const update = (patch: Partial<BatchLTLBulkEditValues>) =>
    Object.entries(patch).forEach(([field, value]) =>
      setValue(field as keyof BatchLTLBulkEditValues, value)
    );

  return (
    <FormProvider {...formMethods}>
      <Flex
        direction='col'
        gap='sm'
        className='w-full rounded-md border border-info-300 bg-info-50 p-2.5'
        data-testid='batch-ltl-bulk-edit'
      >
        <Typography variant='body-xs' weight='semibold'>
          Edit {selectedCount} selected{' '}
          {selectedCount === 1 ? 'shipment' : 'shipments'}
        </Typography>
        <Typography variant='body-xxs' textColor='muted'>
          Blank fields stay as they are on each shipment.
        </Typography>

        <Grid cols='2' gap='sm' className='w-full'>
          <Flex direction='col' gap='xs' className='min-w-0'>
            <Label htmlFor='batch-ltl-bulk-origin' name=''>
              Origin
            </Label>
            <Input
              id='batch-ltl-bulk-origin'
              aria-label='Bulk origin'
              placeholder='City, State or ZIP'
              {...register('originLocation')}
            />
          </Flex>
          <Flex direction='col' gap='xs' className='min-w-0'>
            <Label htmlFor='batch-ltl-bulk-destination' name=''>
              Destination
            </Label>
            <Input
              id='batch-ltl-bulk-destination'
              aria-label='Bulk destination'
              placeholder='City, State or ZIP'
              {...register('destinationLocation')}
            />
          </Flex>
          <div className='col-span-2'>
            <DateTimeInput
              control={control}
              name='pickupDate'
              label='Pickup ready'
              hideAIHint
              containerClassName='w-full gap-0'
              timezone={TAI_WALL_CLOCK_TIMEZONE}
            />
          </div>
          <div className='col-span-2'>
            <DateTimeInput
              control={control}
              name='pickupCloseDate'
              label='Pickup close'
              hideAIHint
              containerClassName='w-full gap-0'
              timezone={TAI_WALL_CLOCK_TIMEZONE}
            />
          </div>
          <Flex direction='col' gap='xs' className='min-w-0'>
            <Label name=''>Freight class</Label>
            <Select
              value={values.freightClass}
              onValueChange={(freightClass) => update({ freightClass })}
            >
              <SelectTrigger
                aria-label='Bulk freight class'
                className='h-8 px-2'
              >
                <SelectValue placeholder='Keep' />
              </SelectTrigger>
              <SelectContent>
                {LTL_FREIGHT_CLASSES.map((freightClass) => (
                  <SelectItem key={freightClass} value={freightClass}>
                    {freightClass}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Flex>
          <Flex direction='col' gap='xs' className='min-w-0'>
            <Label name=''>Stackable</Label>
            <Select
              value={values.stackable}
              onValueChange={(stackable) =>
                update({ stackable: stackable as BatchLTLStackableEdit })
              }
            >
              <SelectTrigger aria-label='Bulk stackable' className='h-8 px-2'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BatchLTLStackableEdit.Unchanged}>
                  Keep
                </SelectItem>
                <SelectItem value={BatchLTLStackableEdit.Stackable}>
                  Yes
                </SelectItem>
                <SelectItem value={BatchLTLStackableEdit.NotStackable}>
                  No
                </SelectItem>
              </SelectContent>
            </Select>
          </Flex>
          <Flex direction='col' gap='xs' className='col-span-2 min-w-0'>
            <Label name=''>Add accessorial</Label>
            <Select
              value={values.addAccessorialCode}
              onValueChange={(addAccessorialCode) =>
                update({ addAccessorialCode })
              }
            >
              <SelectTrigger
                aria-label='Bulk add accessorial'
                className='h-8 px-2'
              >
                <SelectValue placeholder='None' />
              </SelectTrigger>
              <SelectContent>
                {accessorials.map((accessorial) => (
                  <SelectItem key={accessorial.code} value={accessorial.code}>
                    {accessorial.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Flex>
          <Flex direction='col' gap='xs' className='col-span-2 min-w-0'>
            <Label name=''>Remove accessorial</Label>
            <Select
              value={values.removeAccessorialCode}
              onValueChange={(removeAccessorialCode) =>
                update({ removeAccessorialCode })
              }
              disabled={selectedAccessorials.length === 0}
            >
              <SelectTrigger
                aria-label='Bulk remove accessorial'
                className='h-8 px-2'
              >
                <SelectValue placeholder='None' />
              </SelectTrigger>
              <SelectContent>
                {selectedAccessorials.map((accessorial) => (
                  <SelectItem key={accessorial.code} value={accessorial.code}>
                    {accessorial.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Flex>
        </Grid>

        <Flex justify='end' gap='sm' className='w-full'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 px-3 text-xs'
            onClick={onCancel}
            buttonNamePosthog={null}
          >
            Cancel
          </Button>
          <Button
            type='button'
            size='sm'
            className='h-7 px-3 text-xs'
            onClick={() => onApply(getValues())}
            buttonNamePosthog={null}
          >
            Apply to {selectedCount}
          </Button>
        </Flex>
      </Flex>
    </FormProvider>
  );
}
